import { isBearerExpired } from '@/auth/core/isBearerExpired';
import { markSessionExpiredNotice } from '@/auth/sessionNotice';
import type { AuthSession, LoginSuccessResponse } from '@/auth/types';

const STORAGE_KEYS = {
  bearerToken: 'agents.bearerToken',
  companyID: 'agents.companyID',
  employeeID: 'agents.employeeID',
  csrfToken: 'agents.csrfToken',
} as const;

/**
 * Build session from a tokens-only login response.
 * @param {LoginSuccessResponse} response
 * @returns {AuthSession}
 */
export function sessionFromLoginResponse(response: LoginSuccessResponse): AuthSession {
  return {
    companyID: response.companyID,
    employeeID: response.employeeID,
    bearerToken: response.bearerToken,
    csrfToken: response.csrfToken ?? null,
  };
}

/**
 * Persist session to sessionStorage (no secrets in logs).
 * @param {AuthSession} session
 */
export function persistSession(session: AuthSession): void {
  sessionStorage.setItem(STORAGE_KEYS.bearerToken, session.bearerToken);
  sessionStorage.setItem(STORAGE_KEYS.companyID, String(session.companyID));
  sessionStorage.setItem(STORAGE_KEYS.employeeID, String(session.employeeID));
  if (session.csrfToken) {
    sessionStorage.setItem(STORAGE_KEYS.csrfToken, session.csrfToken);
  } else {
    sessionStorage.removeItem(STORAGE_KEYS.csrfToken);
  }
}

/**
 * Load session from sessionStorage if present and consistent.
 * @returns {AuthSession | null}
 */
export function loadPersistedSession(): AuthSession | null {
  const bearerToken = sessionStorage.getItem(STORAGE_KEYS.bearerToken);
  const companyIDRaw = sessionStorage.getItem(STORAGE_KEYS.companyID);
  const employeeIDRaw = sessionStorage.getItem(STORAGE_KEYS.employeeID);

  if (!bearerToken || !companyIDRaw || !employeeIDRaw) {
    return null;
  }

  const companyID = Number.parseInt(companyIDRaw, 10);
  const employeeID = Number.parseInt(employeeIDRaw, 10);

  if (Number.isNaN(companyID) || Number.isNaN(employeeID)) {
    clearPersistedSession();
    return null;
  }

  if (isBearerExpired(bearerToken)) {
    clearPersistedSession();
    markSessionExpiredNotice();
    return null;
  }

  return {
    bearerToken,
    companyID,
    employeeID,
    csrfToken: sessionStorage.getItem(STORAGE_KEYS.csrfToken),
  };
}

/**
 * Drop the current session when the bearer is expired or invalid.
 * @param {AuthSession | null} session
 * @returns {boolean} True when the session was cleared.
 */
export function clearSessionIfExpired(session: AuthSession | null): boolean {
  if (!session) {
    return false;
  }

  if (!isBearerExpired(session.bearerToken)) {
    return false;
  }

  clearPersistedSession();
  markSessionExpiredNotice();
  return true;
}

/** Remove persisted session. */
export function clearPersistedSession(): void {
  sessionStorage.removeItem(STORAGE_KEYS.bearerToken);
  sessionStorage.removeItem(STORAGE_KEYS.companyID);
  sessionStorage.removeItem(STORAGE_KEYS.employeeID);
  sessionStorage.removeItem(STORAGE_KEYS.csrfToken);
}
